import React, { useState, useEffect } from 'react';
import { Container, Image, Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Admin } from '../../../types';
import AdminDetailsModal from '../common/AdminDetailsModal';
import AdminChangePasswordModal from '../common/AdminChangePasswordModal';
import './AdminHeader.css';
import axios from '../../../api/axios';
import { ApiError } from '../../../types/errors';

interface AdminUpdateEvent extends Event {
  detail: {
    name: string;
    email: string;
  };
}

declare global {
  interface WindowEventMap {
    adminUpdated: AdminUpdateEvent;
  }
}

const AdminHeader: React.FC = () => {
  const navigate = useNavigate();
  const [showAdminDetailsModal, setShowAdminDetailsModal] = useState<boolean>(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState<boolean>(false);
  const [currentAdmin, setCurrentAdmin] = useState<Admin | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    const adminStr = localStorage.getItem('admin');
    if (adminStr) {
      try {
        const adminData = JSON.parse(adminStr);
        setCurrentAdmin(adminData);
      } catch (error: unknown) {
        console.error('Error parsing admin data:', error);
        navigate('/admin');
      }
    } else {
      navigate('/admin');
    }
  }, [navigate]);

  useEffect(() => {
    const handleAdminUpdate = (event: AdminUpdateEvent) => {
      setCurrentAdmin(prev => {
        if (!prev) return null;
        return {
          ...prev,
          name: event.detail.name,
          email: event.detail.email
        };
      });
    };

    window.addEventListener('adminUpdated', handleAdminUpdate);

    return () => {
      window.removeEventListener('adminUpdated', handleAdminUpdate);
    };
  }, []);

  const handleLogout = async (): Promise<void> => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post('/admin/logout', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error: unknown) {
      const axiosError = error as ApiError;
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin');
      navigate('/admin');
    }
  };

  return (
    <div className="header">
      <Container>
        <div className="logo-container">
          <Image 
            src="/logo.svg" 
            alt="ESF Logo" 
            className={`logo-image ${currentAdmin && 'pointer'}`} 
            onClick={() => { currentAdmin && navigate('/admin/clients') }} 
          />
          {currentAdmin && (
            <div className="user-dropdown">
              <Dropdown>
                <Dropdown.Toggle variant="link" id="dropdown-basic" className="company-name-dropdown">
                  {currentAdmin.name}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setShowAdminDetailsModal(true)}>管理者詳細</Dropdown.Item>
                  <Dropdown.Item onClick={() => setShowChangePasswordModal(true)}>パスワード変更</Dropdown.Item>
                  <Dropdown.Item onClick={() => navigate('/admin/surveys')}>アンケート設定</Dropdown.Item>
                  {/*
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => navigate('/admin/clients')}>クライアント管理</Dropdown.Item>
                  <Dropdown.Item onClick={() => navigate('/admin/candidates')}>候補者管理</Dropdown.Item>
                  <Dropdown.Item onClick={() => navigate('/admin/respondents')}>回答者管理</Dropdown.Item>
                  <Dropdown.Item onClick={() => navigate('/admin/social-skills')}>選択式質問設定</Dropdown.Item>
                  */}
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleLogout}>ログアウト</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )}
        </div>
      </Container>

      <AdminDetailsModal
        show={showAdminDetailsModal} 
        onHide={() => setShowAdminDetailsModal(false)} 
        admin={currentAdmin} 
      />
      <AdminChangePasswordModal 
        show={showChangePasswordModal} 
        onHide={() => setShowChangePasswordModal(false)} 
      />
    </div>
  );
};

export default AdminHeader;
